import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { openSecureMedia } from '@/lib/secureMedia';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { ArrowLeft, Calendar, FileText, UserPlus, CheckCircle2, XCircle, AlertTriangle, Clock, Send, Upload, Eye, Download, Shield, History, Mail, MessageSquare, Link as LinkIcon, Edit2, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const STAGE_COLORS = { APPLIED: 'bg-blue-100 text-blue-700', SCREENING: 'bg-amber-100 text-amber-700', INTERVIEW: 'bg-purple-100 text-purple-700', OFFER: 'bg-teal-100 text-teal-700', ONBOARDING: 'bg-orange-100 text-orange-700', ONBOARDING_REVIEW: 'bg-pink-100 text-pink-700', APPROVED: 'bg-cyan-100 text-cyan-700', CONVERTED_TO_STAFF: 'bg-emerald-100 text-emerald-700', HIRED: 'bg-emerald-100 text-emerald-700', REJECTED: 'bg-red-100 text-red-700' };
const DOC_STATUS_COLORS = { PENDING: 'bg-slate-400 text-white', APPROVED: 'bg-green-500 text-white', REJECTED: 'bg-red-500 text-white', EXPIRED: 'bg-red-600 text-white', UPLOADED: 'bg-blue-500 text-white' };

export default function CandidateProfilePage() {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const [cand, setCand] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [offers, setOffers] = useState([]);
  const [onboarding, setOnboarding] = useState(null);
  const [docs, setDocs] = useState([]);
  const [reviewHistory, setReviewHistory] = useState([]);
  const [intDialog, setIntDialog] = useState(false);
  const [offerDialog, setOfferDialog] = useState(false);
  const [onboardDialog, setOnboardDialog] = useState(false);
  const [onboardLink, setOnboardLink] = useState('');
  const [convertDialog, setConvertDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [uploadDocDialog, setUploadDocDialog] = useState(false);
  const [docUploadForm, setDocUploadForm] = useState({ document_type: 'OTHER', document_number: '', issue_date: '', expiry_date: '', file: null });
  const [editForm, setEditForm] = useState({});
  const [reviewDialog, setReviewDialog] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [intForm, setIntForm] = useState({ interview_type: 'VIDEO', scheduled_at: '', notes: '' });
  const [offerForm, setOfferForm] = useState({ salary: '', employment_type: 'FULL_TIME', start_date: '' });
  const [convertForm, setConvertForm] = useState({ employment_type: 'CASUAL', staff_category: 'SUPPORT_WORKER', start_date: '', role_name: 'Support Worker' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingMethod, setSendingMethod] = useState('');

  const refresh = useCallback(async () => {
    try {
      const [cR, iR, oR] = await Promise.allSettled([
        api.get(`/candidates/${candidateId}`),
        api.get('/interviews'),
        api.get('/offers'),
      ]);
      if (cR.status === 'fulfilled') setCand(cR.value.data.data);
      setInterviews(iR.status === 'fulfilled' ? (iR.value.data.items || []).filter(i => i.candidate_id === candidateId) : []);
      setOffers(oR.status === 'fulfilled' ? (oR.value.data.items || []).filter(o => o.candidate_id === candidateId) : []);

      // Load onboarding status + docs + review history
      const [obR, docR, revR] = await Promise.allSettled([
        api.get(`/recruitment/onboarding/status/${candidateId}`),
        api.get(`/admin/onboarding/documents?candidate_id=${candidateId}&pageSize=50`),
        api.get(`/recruitment/candidates/${candidateId}/review-history`),
      ]);
      if (obR.status === 'fulfilled') setOnboarding(obR.value.data.data);
      if (docR.status === 'fulfilled') setDocs(docR.value.data.items || []);
      if (revR.status === 'fulfilled') setReviewHistory(revR.value.data.data || []);
    } catch { toast.error('Failed to load'); }
  }, [candidateId]);

  useEffect(() => { refresh(); }, [refresh]);

  const sendOnboarding = async (method) => {
    setSendingMethod(method);
    try {
      const res = await api.post('/recruitment/onboarding/send', { candidate_id: candidateId, send_method: method });
      setOnboardLink(res.data.data.onboarding_url);
      if (method === 'COPY') navigator.clipboard.writeText(res.data.data.onboarding_url).catch(() => {});
      toast.success(method === 'COPY' ? 'Link copied!' : `Sent via ${method}`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.error?.message || 'Failed'); }
    setSendingMethod('');
  };

  const reviewOnboarding = async (status) => {
    try {
      await api.post(`/recruitment/onboarding/review/${candidateId}`, { status, review_notes: reviewNotes });
      toast.success(`Onboarding ${status.toLowerCase()}`);
      setReviewDialog(null); setReviewNotes(''); refresh();
    } catch (e) { toast.error(e.response?.data?.error?.message || 'Failed'); }
  };

  const reviewDocument = async (docId, status) => {
    try {
      await api.post(`/admin/document/review/${docId}`, { status, notes: reviewNotes });
      toast.success(`Document ${status.toLowerCase()}`);
      setReviewDialog(null); setReviewNotes(''); refresh();
    } catch (e) { toast.error(e.response?.data?.error?.message || 'Failed'); }
  };

  const convertToStaff = async () => {
    setLoading(true);
    try {
      const elig = await api.get(`/candidates/${candidateId}/conversion-eligibility`);
      if (!elig.data.data.eligible) { toast.error('Not eligible: ' + elig.data.data.reasons.join(', ')); setLoading(false); return; }
      await api.post(`/candidates/${candidateId}/hire`, convertForm);
      toast.success('Converted to staff!'); setConvertDialog(false); refresh();
    } catch (e) { toast.error(e.response?.data?.error?.message || e.response?.data?.detail?.error?.message || 'Failed'); }
    setLoading(false);
  };

  const handleEditCandidate = async () => {
    try {
      await api.put(`/candidates/${candidateId}`, editForm);
      toast.success('Candidate updated'); setEditDialog(false); refresh();
    } catch (e) { toast.error(e.response?.data?.error?.message || e.response?.data?.detail || 'Update failed'); }
  };

  const handleDeleteCandidate = async () => {
    try {
      await api.post(`/candidates/${candidateId}/archive`);
      toast.success('Candidate archived'); navigate('/hr/recruitment');
    } catch (e) { toast.error(e.response?.data?.error?.message || e.response?.data?.detail || 'Delete failed'); }
  };

  const handleUploadCandDoc = async () => {
    if (!docUploadForm.file) { toast.error('Select a file'); return; }
    setSaving(true);
    try {
      const inv = onboarding?.invite;
      const token = inv?.token;
      if (token) {
        const fd = new FormData();
        fd.append('token', token);
        fd.append('document_type', docUploadForm.document_type);
        fd.append('file', docUploadForm.file);
        if (docUploadForm.document_number) fd.append('document_number', docUploadForm.document_number);
        if (docUploadForm.issue_date) fd.append('issue_date', docUploadForm.issue_date);
        if (docUploadForm.expiry_date) fd.append('expiry_date', docUploadForm.expiry_date);
        await api.post('/onboarding/document/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        // Direct upload without token (admin upload)
        const fd = new FormData();
        fd.append('candidate_id', candidateId);
        fd.append('document_type', docUploadForm.document_type);
        fd.append('file', docUploadForm.file);
        if (docUploadForm.document_number) fd.append('document_number', docUploadForm.document_number);
        if (docUploadForm.issue_date) fd.append('issue_date', docUploadForm.issue_date);
        if (docUploadForm.expiry_date) fd.append('expiry_date', docUploadForm.expiry_date);
        await api.post(`/recruitment/candidates/${candidateId}/documents/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('Document uploaded'); setUploadDocDialog(false);
      setDocUploadForm({ document_type: 'OTHER', document_number: '', issue_date: '', expiry_date: '', file: null });
      refresh();
    } catch (e) { toast.error(e.response?.data?.error?.message || 'Upload failed'); }
    setSaving(false);
  };

  if (!cand) return <div className="py-12 text-center text-muted-foreground">Loading...</div>;

  const isConverted = cand.conversion_status === 'CONVERTED';

  return (
    <div className="space-y-6 max-w-5xl" data-testid="candidate-profile-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/hr/recruitment')} className="-ml-2"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">{cand.first_name} {cand.last_name}</h1>
          <p className="text-muted-foreground">{cand.email} · {cand.phone || '-'} · {cand.candidate_no}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex gap-2">
            {!isConverted && <Button size="sm" variant="outline" onClick={() => { setEditForm({ first_name: cand.first_name, last_name: cand.last_name, email: cand.email, phone: cand.phone, notes: cand.notes }); setEditDialog(true); }} data-testid="edit-candidate-btn"><Edit2 className="h-3 w-3 mr-1" />Edit</Button>}
            <Button size="sm" variant="destructive" onClick={() => setDeleteConfirm(true)} data-testid="delete-candidate-btn"><Trash2 className="h-3 w-3 mr-1" />Archive</Button>
          </div>
          <Badge className={STAGE_COLORS[cand.stage] || ''}>{cand.stage?.replace(/_/g, ' ')}</Badge>
          <div className="flex gap-1">
            {cand.onboarding_status && cand.onboarding_status !== 'DRAFT' && <Badge variant="outline" className="text-xs">{cand.onboarding_status}</Badge>}
            {cand.conversion_status && cand.conversion_status !== 'NOT_ELIGIBLE' && <Badge variant="outline" className="text-xs">{cand.conversion_status}</Badge>}
          </div>
          {isConverted && cand.converted_staff_id && <Link to={`/staff/${cand.converted_staff_id}`} className="text-xs text-primary underline">View Staff Profile →</Link>}
        </div>
      </div>

      <Tabs defaultValue="summary">
        <TabsList className="flex-wrap">
          <TabsTrigger value="summary" data-testid="tab-summary">Summary</TabsTrigger>
          <TabsTrigger value="recruitment" data-testid="tab-recruitment">Recruitment</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="tab-onboarding">Onboarding</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents ({docs.length})</TabsTrigger>
          <TabsTrigger value="review" data-testid="tab-review">Review History ({reviewHistory.length})</TabsTrigger>
        </TabsList>

        {/* SUMMARY TAB */}
        <TabsContent value="summary" className="space-y-4 mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card><CardContent className="py-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Candidate ID</span><span className="font-mono">{cand.candidate_no}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Position</span><span>{cand.position_id?.slice(0, 12)}...</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Source</span><StatusBadge status={cand.source} /></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Stage</span><Badge className={STAGE_COLORS[cand.stage]}>{cand.stage?.replace(/_/g, ' ')}</Badge></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Onboarding</span><Badge variant="outline">{cand.onboarding_status || 'N/A'}</Badge></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Conversion</span><Badge variant="outline">{cand.conversion_status || 'N/A'}</Badge></div>
              {cand.rating && <div className="flex justify-between"><span className="text-muted-foreground">Rating</span><span>{'★'.repeat(cand.rating)}{'☆'.repeat(5 - cand.rating)}</span></div>}
            </CardContent></Card>
            <Card><CardContent className="py-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span>{cand.email}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Phone</span><span>{cand.phone || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Branch</span><span>{cand.branch_id || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Created</span><span className="font-mono text-xs">{cand.created_at?.slice(0, 10)}</span></div>
              {cand.notes && <div className="pt-2 border-t"><span className="text-muted-foreground">Notes:</span> {cand.notes}</div>}
            </CardContent></Card>
          </div>

          {/* Quick Actions */}
          {!isConverted && (
            <div className="flex gap-2 flex-wrap">
              {['APPLIED', 'SCREENING'].includes(cand.stage) && <Button size="sm" variant="outline" onClick={() => setIntDialog(true)}><Calendar className="h-3 w-3 mr-1" />Schedule Interview</Button>}
              {['INTERVIEW', 'SCREENING'].includes(cand.stage) && <Button size="sm" variant="outline" onClick={() => setOfferDialog(true)}><FileText className="h-3 w-3 mr-1" />Create Offer</Button>}
              {cand.stage === 'OFFER' && <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={() => setOnboardDialog(true)}><Send className="h-3 w-3 mr-1" />Send Onboarding</Button>}
              {['ONBOARDING_REVIEW'].includes(cand.stage) && <>
                <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setReviewDialog('APPROVE')}><CheckCircle2 className="h-3 w-3 mr-1" />Approve</Button>
                <Button size="sm" variant="outline" className="text-amber-600" onClick={() => setReviewDialog('ACTION_REQUIRED')}><AlertTriangle className="h-3 w-3 mr-1" />Request Resubmission</Button>
                <Button size="sm" variant="destructive" onClick={() => setReviewDialog('REJECT')}><XCircle className="h-3 w-3 mr-1" />Reject</Button>
              </>}
              {cand.stage === 'APPROVED' && <Button size="sm" onClick={() => setConvertDialog(true)}><UserPlus className="h-3 w-3 mr-1" />Convert to Staff</Button>}
            </div>
          )}
        </TabsContent>

        {/* RECRUITMENT TAB */}
        <TabsContent value="recruitment" className="space-y-4 mt-4">
          <Card><CardHeader className="pb-2"><CardTitle className="text-base">Interviews ({interviews.length})</CardTitle></CardHeader>
            <CardContent>{interviews.length === 0 ? <p className="text-sm text-muted-foreground">No interviews</p> : interviews.map(i => (
              <div key={i.id} className="flex items-center justify-between border-b last:border-0 py-2">
                <div><StatusBadge status={i.interview_type} /> <span className="font-mono text-xs ml-2">{i.scheduled_at?.replace('T', ' ').slice(0, 16)}</span></div>
                <div className="flex items-center gap-2"><StatusBadge status={i.status} />{i.rating && <span>{'★'.repeat(i.rating)}</span>}</div>
              </div>
            ))}</CardContent>
          </Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-base">Offers ({offers.length})</CardTitle></CardHeader>
            <CardContent>{offers.length === 0 ? <p className="text-sm text-muted-foreground">No offers</p> : offers.map(o => (
              <div key={o.id} className="flex items-center justify-between border-b last:border-0 py-2">
                <div><span className="font-medium">{o.salary || '-'}</span> <span className="text-xs text-muted-foreground ml-2">{o.employment_type} · Start: {o.start_date || '-'}</span></div>
                <StatusBadge status={o.status} />
              </div>
            ))}</CardContent>
          </Card>
        </TabsContent>

        {/* ONBOARDING TAB */}
        <TabsContent value="onboarding" className="space-y-4 mt-4">
          {onboarding?.invite ? (
            <Card><CardHeader className="pb-2"><CardTitle className="text-base">Invitation</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-1">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-muted-foreground">Status:</span> <Badge variant="outline">{onboarding.invite.status}</Badge></div>
                  <div><span className="text-muted-foreground">Method:</span> {onboarding.invite.send_method}</div>
                  <div><span className="text-muted-foreground">Sent:</span> {onboarding.invite.email_sent_at?.slice(0, 16) || onboarding.invite.sms_sent_at?.slice(0, 16) || '-'}</div>
                  <div><span className="text-muted-foreground">Opened:</span> {onboarding.invite.opened_at?.slice(0, 16) || 'Not yet'}</div>
                  <div><span className="text-muted-foreground">Expires:</span> {onboarding.invite.expires_at?.slice(0, 10)}</div>
                </div>
              </CardContent>
            </Card>
          ) : <p className="text-muted-foreground">No onboarding invitation sent yet</p>}

          {onboarding?.form && (
            <Card><CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <CardTitle className="text-base">Application Form</CardTitle>
                {onboarding?.form?.review_status && ['SUBMITTED', 'APPROVED', 'ACTION_REQUIRED', 'IN_REVIEW'].includes(onboarding.form.review_status) && (
                  <Button size="sm" variant="outline" onClick={() => { setEditDialog(true); setEditForm(onboarding.form); }} data-testid="edit-application-btn"><Edit2 className="h-3 w-3 mr-1" />Edit Application</Button>
                )}
              </div>
              {onboarding?.form?.edited_after_submission && (
                <p className="text-xs text-amber-600 mt-1">This application was edited after submission. Last edited by {onboarding.form.last_edited_by?.slice(0, 8)}... at {onboarding.form.last_edited_at?.slice(0, 16)}</p>
              )}
            </CardHeader>
              <CardContent className="text-sm space-y-2">
                <div className="flex items-center gap-3">
                  <Badge variant="outline">{onboarding.form.review_status}</Badge>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${onboarding.form.progress_percent || 0}%` }} /></div>
                  <span className="text-xs">{onboarding.form.progress_percent || 0}%</span>
                </div>
                {onboarding.form.submitted_at && <p className="text-xs text-muted-foreground">Submitted: {onboarding.form.submitted_at?.slice(0, 16)}</p>}
                {onboarding.form.reviewed_at && <p className="text-xs text-muted-foreground">Reviewed: {onboarding.form.reviewed_at?.slice(0, 16)} by {onboarding.form.reviewed_by || '-'}</p>}

                {/* Form sections accordion */}
                {['personal_details', 'work_eligibility', 'employment_details', 'compliance_checks', 'bank_details', 'declarations'].map(key => {
                  const data = onboarding.form[key];
                  if (!data || Object.keys(data).length === 0) return null;
                  return (
                    <details key={key} className="border rounded-lg p-2">
                      <summary className="text-xs font-medium cursor-pointer">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</summary>
                      <pre className="text-[11px] text-muted-foreground mt-1 whitespace-pre-wrap">{JSON.stringify(data, null, 2)}</pre>
                    </details>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* DOCUMENTS TAB */}
        <TabsContent value="documents" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">{docs.length} documents · {docs.filter(d => d.status === 'APPROVED').length} approved</p>
            <Button size="sm" onClick={() => setUploadDocDialog(true)} data-testid="add-cand-doc-btn"><Plus className="h-3 w-3 mr-1" />Add Document</Button>
          </div>
          {docs.length === 0 ? <p className="text-center text-muted-foreground py-6">No documents uploaded</p> : (
            <div className="space-y-2">
              {docs.map(d => {
                const expiry = d.expiry_date?.slice(0, 10);
                let daysLeft = null;
                let expiryColor = '';
                if (expiry) {
                  daysLeft = Math.ceil((new Date(expiry) - new Date()) / (1000 * 60 * 60 * 24));
                  if (daysLeft < 0) expiryColor = 'text-red-600 bg-red-50';
                  else if (daysLeft <= 7) expiryColor = 'text-red-500 bg-red-50';
                  else if (daysLeft <= 30) expiryColor = 'text-amber-600 bg-amber-50';
                  else if (daysLeft <= 60) expiryColor = 'text-yellow-600 bg-yellow-50';
                }
                return (
                  <Card key={d.id} className={daysLeft !== null && daysLeft <= 7 ? 'border-red-300' : daysLeft !== null && daysLeft <= 30 ? 'border-amber-200' : ''}>
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          <Badge className={DOC_STATUS_COLORS[d.status] || ''}>{d.status}</Badge>
                          <div className="min-w-0">
                            <p className="text-sm font-medium">{d.document_type?.replace(/_/g, ' ')}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{d.file_name || '-'}{d.document_number ? ` · #${d.document_number}` : ''}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {d.file_url && <Button size="sm" variant="ghost" onClick={async () => {
                            try {
                              await openSecureMedia(d.file_url);
                            } catch { toast.error('Unable to open document'); }
                          }}><Eye className="h-3 w-3" /></Button>}
                          {d.status === 'PENDING' && <>
                            <Button size="sm" variant="default" className="h-7 text-xs bg-green-600" onClick={() => reviewDocument(d.id, 'APPROVED')}>Approve</Button>
                            <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => reviewDocument(d.id, 'REJECTED')}>Reject</Button>
                          </>}
                        </div>
                      </div>
                      {/* Expiry Display */}
                      {expiry && (
                        <div className={`mt-2 flex items-center gap-2 px-3 py-1.5 rounded-md text-xs ${expiryColor || 'text-muted-foreground bg-muted/50'}`}>
                          <Clock className="h-3.5 w-3.5 shrink-0" />
                          <span className="font-medium">Expiry: {expiry}</span>
                          <span>·</span>
                          {daysLeft < 0 ? (
                            <span className="font-bold">EXPIRED ({Math.abs(daysLeft)} days ago)</span>
                          ) : daysLeft === 0 ? (
                            <span className="font-bold">EXPIRES TODAY</span>
                          ) : daysLeft <= 7 ? (
                            <span className="font-bold">{daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining — CRITICAL</span>
                          ) : daysLeft <= 30 ? (
                            <span className="font-semibold">{daysLeft} days remaining — URGENT</span>
                          ) : daysLeft <= 60 ? (
                            <span>{daysLeft} days remaining — WARNING</span>
                          ) : (
                            <span>{daysLeft} days remaining</span>
                          )}
                        </div>
                      )}
                      {d.issue_date && (
                        <p className="text-[11px] text-muted-foreground mt-1 ml-1">Issued: {d.issue_date?.slice(0, 10)}</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* REVIEW HISTORY TAB */}
        <TabsContent value="review" className="space-y-4 mt-4">
          {reviewHistory.length === 0 ? <p className="text-center text-muted-foreground py-6">No review events</p> : (
            <div className="space-y-2">
              {reviewHistory.map(e => (
                <Card key={e.id}>
                  <CardContent className="py-3 text-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{e.action?.replace(/_/g, ' ')}</Badge>
                        <span className="text-muted-foreground">{e.target_type}</span>
                      </div>
                      <span className="text-xs text-muted-foreground font-mono">{e.created_at?.slice(0, 16)}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {e.previous_status && <Badge variant="outline" className="text-xs">{e.previous_status}</Badge>}
                      {e.previous_status && <span className="text-xs">→</span>}
                      <Badge variant="outline" className="text-xs">{e.new_status}</Badge>
                      {e.actor_name && <span className="text-xs text-muted-foreground ml-2">by {e.actor_name}</span>}
                    </div>
                    {e.notes && <p className="text-xs text-muted-foreground mt-1">{e.notes}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* DIALOGS */}

      {/* Send Onboarding */}
      <Dialog open={onboardDialog} onOpenChange={setOnboardDialog}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Send Onboarding Link</DialogTitle><DialogDescription>to {cand.first_name} {cand.last_name} ({cand.email})</DialogDescription></DialogHeader>
          <div className="grid grid-cols-3 gap-2">
            <Button variant="outline" className="h-auto py-4 flex-col gap-2" disabled={!!sendingMethod} onClick={() => sendOnboarding('EMAIL')}><Mail className="h-5 w-5" /><span className="text-xs">{sendingMethod === 'EMAIL' ? '...' : 'Email'}</span></Button>
            <Button variant="outline" className="h-auto py-4 flex-col gap-2" disabled={!!sendingMethod} onClick={() => sendOnboarding('SMS')}><MessageSquare className="h-5 w-5" /><span className="text-xs">{sendingMethod === 'SMS' ? '...' : 'SMS'}</span></Button>
            <Button variant="outline" className="h-auto py-4 flex-col gap-2 border-primary" disabled={!!sendingMethod} onClick={() => sendOnboarding('COPY')}><LinkIcon className="h-5 w-5" /><span className="text-xs">{sendingMethod === 'COPY' ? '...' : 'Copy Link'}</span></Button>
          </div>
          {onboardLink && <div className="mt-3 space-y-1"><Input value={onboardLink} readOnly className="text-xs font-mono" /><Button size="sm" variant="outline" className="w-full" onClick={() => { navigator.clipboard.writeText(onboardLink); toast.success('Copied!'); }}>Copy Link</Button></div>}
        </DialogContent>
      </Dialog>

      {/* Review Onboarding */}
      <Dialog open={!!reviewDialog} onOpenChange={() => setReviewDialog(null)}>
        <DialogContent><DialogHeader><DialogTitle>{reviewDialog === 'APPROVE' ? 'Approve Onboarding' : reviewDialog === 'ACTION_REQUIRED' ? 'Request Resubmission' : 'Reject Onboarding'}</DialogTitle></DialogHeader>
          <Textarea placeholder="Review notes..." value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialog(null)}>Cancel</Button>
            <Button variant={reviewDialog === 'REJECT' ? 'destructive' : 'default'} onClick={() => reviewOnboarding(reviewDialog === 'APPROVE' ? 'APPROVED' : reviewDialog)}>
              {reviewDialog === 'APPROVE' ? 'Approve' : reviewDialog === 'ACTION_REQUIRED' ? 'Request Resubmission' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Staff */}
      <Dialog open={convertDialog} onOpenChange={setConvertDialog}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Convert to Staff</DialogTitle><DialogDescription>{cand.first_name} {cand.last_name}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Staff Code</Label><Input value="" readOnly placeholder="Same as Employee ID (automatic)" className="h-8 bg-muted" /></div>
              <div><Label className="text-xs">Role</Label><Input value={convertForm.role_name} onChange={e => setConvertForm({ ...convertForm, role_name: e.target.value })} className="h-8" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Employment Type</Label>
                <Select value={convertForm.employment_type} onValueChange={v => setConvertForm({ ...convertForm, employment_type: v })}><SelectTrigger className="h-8"><SelectValue /></SelectTrigger><SelectContent>{['FULL_TIME', 'PART_TIME', 'CASUAL', 'CONTRACT'].map(t => <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-xs">Category</Label>
                <Select value={convertForm.staff_category} onValueChange={v => setConvertForm({ ...convertForm, staff_category: v })}><SelectTrigger className="h-8"><SelectValue /></SelectTrigger><SelectContent>{['SUPPORT_WORKER', 'NURSE', 'ADMIN', 'HR', 'FINANCE', 'OTHER'].map(t => <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div><Label className="text-xs">Start Date</Label><Input type="date" value={convertForm.start_date} onChange={e => setConvertForm({ ...convertForm, start_date: e.target.value })} className="h-8" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setConvertDialog(false)}>Cancel</Button><Button onClick={convertToStaff} disabled={loading}>{loading ? 'Converting...' : 'Confirm Conversion'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Interview */}
      <Dialog open={intDialog} onOpenChange={setIntDialog}>
        <DialogContent><DialogHeader><DialogTitle>Schedule Interview</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={intForm.interview_type} onValueChange={v => setIntForm({ ...intForm, interview_type: v })}><SelectTrigger className="h-8"><SelectValue /></SelectTrigger><SelectContent>{['PHONE', 'VIDEO', 'IN_PERSON'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            <Input type="datetime-local" value={intForm.scheduled_at} onChange={e => setIntForm({ ...intForm, scheduled_at: e.target.value })} className="h-8" />
            <Input value={intForm.notes} onChange={e => setIntForm({ ...intForm, notes: e.target.value })} placeholder="Notes" className="h-8" />
            <Button className="w-full" size="sm" onClick={async () => { try { await api.post('/interviews', { candidate_id: candidateId, position_id: cand?.position_id, ...intForm }); toast.success('Scheduled'); setIntDialog(false); refresh(); } catch { toast.error('Failed'); } }}>Schedule</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Offer */}
      <Dialog open={offerDialog} onOpenChange={setOfferDialog}>
        <DialogContent><DialogHeader><DialogTitle>Create Offer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={offerForm.salary} onChange={e => setOfferForm({ ...offerForm, salary: e.target.value })} placeholder="Salary e.g. $65,000" className="h-8" />
            <Select value={offerForm.employment_type} onValueChange={v => setOfferForm({ ...offerForm, employment_type: v })}><SelectTrigger className="h-8"><SelectValue /></SelectTrigger><SelectContent>{['FULL_TIME', 'PART_TIME', 'CASUAL', 'CONTRACT'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            <Input type="date" value={offerForm.start_date} onChange={e => setOfferForm({ ...offerForm, start_date: e.target.value })} className="h-8" />
            <Button className="w-full" size="sm" onClick={async () => { try { await api.post('/offers', { candidate_id: candidateId, position_id: cand?.position_id, ...offerForm }); toast.success('Offer created'); setOfferDialog(false); refresh(); } catch { toast.error('Failed'); } }}>Create Offer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Candidate */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto"><DialogHeader><DialogTitle>Edit {editForm.personal_details ? 'Application' : 'Candidate'}</DialogTitle>
          {editForm.personal_details && <p className="text-xs text-amber-600">This application was already submitted. Changes will be audit logged.</p>}
        </DialogHeader>
          {editForm.personal_details ? (
            /* Application edit mode */
            <div className="space-y-3">
              {['personal_details', 'work_eligibility', 'employment_details', 'compliance_checks', 'bank_details', 'declarations'].map(section => {
                const data = editForm[section];
                if (!data || Object.keys(data).length === 0) return null;
                return (
                  <details key={section} className="border rounded-lg p-2">
                    <summary className="text-sm font-medium cursor-pointer">{section.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</summary>
                    <div className="space-y-2 mt-2">
                      {Object.entries(data).map(([k, v]) => (
                        <div key={k}><Label className="text-xs">{k.replace(/_/g, ' ')}</Label>
                          <Input value={typeof v === 'boolean' ? (v ? 'true' : 'false') : Array.isArray(v) ? v.join(', ') : String(v || '')}
                            onChange={e => setEditForm(prev => ({ ...prev, [section]: { ...prev[section], [k]: e.target.value } }))} className="h-8 text-sm" />
                        </div>
                      ))}
                    </div>
                  </details>
                );
              })}
              <DialogFooter><Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
                <Button onClick={async () => {
                  try {
                    const payload = {};
                    for (const s of ['personal_details', 'work_eligibility', 'employment_details', 'compliance_checks', 'bank_details', 'declarations']) {
                      if (editForm[s]) payload[s] = editForm[s];
                    }
                    await api.put(`/recruitment/candidates/${candidateId}/application`, payload);
                    toast.success('Application updated'); setEditDialog(false); refresh();
                  } catch (e) { toast.error(e.response?.data?.error?.message || 'Update failed'); }
                }}>Save Changes</Button>
              </DialogFooter>
            </div>
          ) : (
            /* Basic candidate edit mode */
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>First Name</Label><Input value={editForm.first_name || ''} onChange={e => setEditForm({ ...editForm, first_name: e.target.value })} /></div>
                <div><Label>Last Name</Label><Input value={editForm.last_name || ''} onChange={e => setEditForm({ ...editForm, last_name: e.target.value })} /></div>
              </div>
              <div><Label>Email</Label><Input value={editForm.email || ''} onChange={e => setEditForm({ ...editForm, email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={editForm.phone || ''} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} /></div>
              <div><Label>Notes</Label><Input value={editForm.notes || ''} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} /></div>
              <DialogFooter><Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button><Button onClick={handleEditCandidate}>Save</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete/Archive Confirm */}
      <ConfirmDialog open={deleteConfirm} onOpenChange={setDeleteConfirm} title="Archive Candidate" description={`Are you sure you want to archive ${cand.first_name} ${cand.last_name}?`} confirmLabel="Archive" variant="destructive" onConfirm={handleDeleteCandidate} />

      {/* Upload Document */}
      <Dialog open={uploadDocDialog} onOpenChange={setUploadDocDialog}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Document Type</Label>
              <Select value={docUploadForm.document_type} onValueChange={v => setDocUploadForm({ ...docUploadForm, document_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{['RESUME','PHOTO_ID','NDIS_WORKER_SCREENING','WWCC','POLICE_CHECK','FIRST_AID','CPR','DRIVER_LICENCE','QUALIFICATIONS','EMPLOYMENT_AGREEMENT','CODE_OF_CONDUCT','NDIS_DECLARATION','OTHER'].map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Document Number</Label><Input value={docUploadForm.document_number} onChange={e => setDocUploadForm({ ...docUploadForm, document_number: e.target.value })} placeholder="Optional" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Issue Date</Label><Input type="date" value={docUploadForm.issue_date} onChange={e => setDocUploadForm({ ...docUploadForm, issue_date: e.target.value })} /></div>
              <div><Label>Expiry Date</Label><Input type="date" value={docUploadForm.expiry_date} onChange={e => setDocUploadForm({ ...docUploadForm, expiry_date: e.target.value })} /></div>
            </div>
            <div><Label>File</Label><Input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => setDocUploadForm({ ...docUploadForm, file: e.target.files?.[0] || null })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setUploadDocDialog(false)}>Cancel</Button><Button onClick={handleUploadCandDoc} disabled={saving || !docUploadForm.file}>{saving ? 'Uploading...' : 'Upload'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
